import { useMemo, useState, type FormEvent, type ReactElement } from "react";
import { UiButton, UiFilterBar, UiKpiCard, SvgIcon } from "@kuroo/ui";

type LeadStatus = "new" | "contacted" | "qualified" | "won";
type LeadSource = "Web" | "Referido" | "LinkedIn" | "Importación";

type Lead = {
  id: string;
  name: string;
  email: string;
  company: string;
  status: LeadStatus;
  source: LeadSource;
  owner: string;
  updated: string;
  valueCents: number;
};

const statusLabels: Record<LeadStatus, string> = {
  new: "Nuevo",
  contacted: "Contactado",
  qualified: "Calificado",
  won: "Ganado"
};

const initialLeads: Lead[] = [
  { id: "lead-ana", name: "Ana García", email: "ana@lumina.es", company: "Lumina Studio", status: "new", source: "Web", owner: "Luciano", updated: "Hoy, 09:40", valueCents: 420000 },
  { id: "lead-bruno", name: "Bruno Silva", email: "bruno@northstar.io", company: "Northstar", status: "contacted", source: "LinkedIn", owner: "Marta", updated: "Hoy, 08:55", valueCents: 680000 },
  { id: "lead-carla", name: "Carla Méndez", email: "carla@atelier.co", company: "Atelier & Co.", status: "qualified", source: "Referido", owner: "Luciano", updated: "Ayer, 17:20", valueCents: 1250000 },
  { id: "lead-diego", name: "Diego Ruiz", email: "diego@orbital.dev", company: "Orbital", status: "won", source: "Importación", owner: "Marta", updated: "Ayer, 15:05", valueCents: 890000 },
  { id: "lead-elena", name: "Elena Torres", email: "elena@cobalto.es", company: "Cobalto", status: "qualified", source: "Web", owner: "Luciano", updated: "Lun, 11:30", valueCents: 540000 }
];

const leadSources: LeadSource[] = ["Web", "Referido", "LinkedIn", "Importación"];
const leadOwners = ["Luciano", "Marta"];

function statusClass(status: LeadStatus): string {
  return `leads-status leads-status-${status}`;
}

function euro(cents: number): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(cents / 100);
}

function LeadsDashboardPage(): ReactElement {
  const [leads, setLeads] = useState<Lead[]>(initialLeads);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<LeadStatus | "all">("all");
  const [source, setSource] = useState<LeadSource | "all">("all");
  const [owner, setOwner] = useState<string | "all">("all");
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [notice, setNotice] = useState<string>();

  const filteredLeads = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("es");
    return leads.filter((lead) => {
      const matchesQuery = !normalized || [lead.name, lead.email, lead.company].some((value) => value.toLocaleLowerCase("es").includes(normalized));
      return matchesQuery && (status === "all" || lead.status === status) && (source === "all" || lead.source === source) && (owner === "all" || lead.owner === owner);
    });
  }, [leads, owner, query, source, status]);

  const metrics = useMemo(() => {
    const qualified = leads.filter((lead) => lead.status === "qualified").length;
    const won = leads.filter((lead) => lead.status === "won").length;
    const pipeline = leads.filter((lead) => lead.status !== "won").reduce((total, lead) => total + lead.valueCents, 0);
    return {
      total: leads.length,
      qualified,
      conversion: leads.length ? Math.round((won / leads.length) * 100) : 0,
      pipeline
    };
  }, [leads]);

  const clearFilters = () => {
    setQuery("");
    setStatus("all");
    setSource("all");
    setOwner("all");
  };

  const createLead = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const name = String(values.get("name") ?? "").trim();
    const email = String(values.get("email") ?? "").trim();
    const company = String(values.get("company") ?? "").trim();
    if (!name || !email || !company) return;
    const next: Lead = {
      id: `lead-${Date.now()}`,
      name,
      email,
      company,
      status: "new",
      source: String(values.get("source") ?? "Web") as LeadSource,
      owner: String(values.get("owner") ?? "Luciano"),
      updated: "Ahora",
      valueCents: Number(values.get("valueCents") ?? 0) * 100
    };
    setLeads((current) => [next, ...current]);
    setNewLeadOpen(false);
    setNotice(`${name} se agregó como nuevo lead.`);
  };

  return <section className="leads-page" aria-labelledby="leads-dashboard-title">
    <header className="leads-heading">
      <div>
        <p className="eyebrow">CRM · Pipeline</p>
        <h1 id="leads-dashboard-title">CRM Leads Dashboard</h1>
        <p className="lede">Seguimiento de oportunidades y actividad comercial.</p>
      </div>
      <UiButton size="sm" onClick={() => setNewLeadOpen(true)}>+ Nuevo Lead</UiButton>
    </header>

    {notice && <div className="toast" role="status">{notice}<button type="button" className="text-button" onClick={() => setNotice(undefined)}>Cerrar</button></div>}

    <div className="leads-kpis" aria-label="Resumen de leads">
      <UiKpiCard className="leads-kpi" label="Total leads" value={metrics.total} delta="+12% frente al periodo anterior" />
      <UiKpiCard className="leads-kpi" label="Calificados" value={metrics.qualified} delta="+8% frente al periodo anterior" />
      <UiKpiCard className="leads-kpi" label="Conversión" value={`${metrics.conversion}%`} delta="+3.2% frente al periodo anterior" />
      <UiKpiCard className="leads-kpi" label="Pipeline" value={euro(metrics.pipeline)} delta="Valor estimado en curso" />
    </div>

    <UiFilterBar className="leads-filterbar">
      <label className="leads-search"><SvgIcon name="search" size={17} /><span className="sr-only">Buscar leads</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar leads..." /></label>
      <label>Estado<select value={status} onChange={(event) => setStatus(event.target.value as LeadStatus | "all")}><option value="all">Todos</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label>Fuente<select value={source} onChange={(event) => setSource(event.target.value as LeadSource | "all")}><option value="all">Todas</option>{leadSources.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
      <label>Owner<select value={owner} onChange={(event) => setOwner(event.target.value)}><option value="all">Todos</option>{leadOwners.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
      <UiButton tone="secondary" size="sm" onClick={clearFilters}>Limpiar</UiButton>
    </UiFilterBar>

    <section className="panel leads-table-panel" aria-labelledby="leads-table-title">
      <div className="leads-table-heading"><div><h2 id="leads-table-title">Leads recientes</h2><span>{filteredLeads.length} de {leads.length} visibles</span></div><span className="leads-updated">Actualizado ahora</span></div>
      <div className="table-wrap"><table className="leads-table"><caption className="sr-only">Lista de leads del pipeline</caption><thead><tr><th>Lead</th><th>Empresa</th><th>Estado</th><th>Fuente</th><th>Owner</th><th>Actualizado</th><th><span className="sr-only">Acciones</span></th></tr></thead><tbody>{filteredLeads.map((lead) => <tr key={lead.id}><td><strong>{lead.name}</strong><small>{lead.email}</small></td><td>{lead.company}</td><td><span className={statusClass(lead.status)}>{statusLabels[lead.status]}</span></td><td>{lead.source}</td><td>{lead.owner}</td><td>{lead.updated}</td><td><button type="button" className="text-button" onClick={() => setNotice(`El detalle de ${lead.name} quedará conectado al flujo de leads.`)}>Ver detalle</button></td></tr>)}</tbody></table></div>
      {!filteredLeads.length && <div className="leads-empty"><strong>No se encontraron leads</strong><span>Probá con otra búsqueda o limpiá los filtros.</span><button type="button" className="text-button" onClick={clearFilters}>Limpiar filtros</button></div>}
    </section>

    <p className="leads-data-note">Vista end-to-end de diseño y UI. Los datos de ejemplo se mantienen locales hasta que exista un endpoint de leads en el contrato de la API.</p>

    {newLeadOpen && <div className="leads-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setNewLeadOpen(false); }}><form className="leads-dialog" role="dialog" aria-modal="true" aria-labelledby="new-lead-title" onSubmit={createLead}><div className="leads-dialog-heading"><div><p className="eyebrow">Pipeline</p><h2 id="new-lead-title">Nuevo lead</h2></div><button type="button" className="icon-button" aria-label="Cerrar" onClick={() => setNewLeadOpen(false)}>×</button></div><div className="form-grid"><label>Nombre<input name="name" autoFocus required placeholder="Nombre y apellido" /></label><label>Email<input name="email" type="email" required placeholder="nombre@empresa.com" /></label><label>Empresa<input name="company" required placeholder="Empresa" /></label><label>Valor estimado<input name="valueCents" type="number" min="0" step="100" defaultValue="0" /></label><label>Fuente<select name="source" defaultValue="Web">{leadSources.map((value) => <option key={value}>{value}</option>)}</select></label><label>Owner<select name="owner" defaultValue="Luciano">{leadOwners.map((value) => <option key={value}>{value}</option>)}</select></label></div><div className="button-row"><UiButton tone="secondary" type="button" onClick={() => setNewLeadOpen(false)}>Cancelar</UiButton><UiButton type="submit">Guardar lead</UiButton></div></form></div>}
  </section>;
}

export { LeadsDashboardPage };
